/**
 * Hook for uploading project thumbnails to Convex storage
 * 
 * This hook monitors the editor overlays and automatically extracts
 * and uploads a thumbnail from the first video/image overlay.
 * 
 * NOTE: Thumbnail is only uploaded when:
 * 1. Project doesn't already have a thumbnail (first time)
 * 2. The first media overlay changes (different source)
 */

import { useEffect, useRef, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import { Id } from '../../../../convex/_generated/dataModel';
import { extractAndUploadThumbnail } from '../utils/general/thumbnail-upload';

interface UseThumbnailUploadOptions {
    /** Whether the project is still loading */
    isLoading?: boolean;
    /** Debounce interval in ms */
    debounceMs?: number;
}

// Session storage key for tracking uploaded thumbnails
const THUMBNAIL_UPLOAD_KEY = 'reelcat-thumbnail-uploaded';

/**
 * Get the session storage key for a specific project
 */
function getSessionKey(projectId: string): string {
    return `${THUMBNAIL_UPLOAD_KEY}-${projectId}`;
}

/**
 * Hook to automatically upload project thumbnail when overlays contain video/image
 */
export function useThumbnailUpload(
    projectId: string | undefined,
    overlays: any[],
    options: UseThumbnailUploadOptions = {}
) {
    const { isLoading = false, debounceMs = 5000 } = options;

    const generateUploadUrl = useMutation(api.projects.generateUploadUrl);
    const updateProjectThumbnail = useMutation(api.projects.updateProjectThumbnail);

    // Query to check if project already has a thumbnail
    const project = useQuery(
        api.projects.getProject,
        projectId ? { projectId: projectId as Id<"projects"> } : "skip"
    );

    const lastUploadedOverlayIdRef = useRef<string | null>(null);
    const uploadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isUploadingRef = useRef(false);
    const hasCheckedExistingThumbnailRef = useRef(false);

    // Initialize lastUploadedOverlayIdRef from sessionStorage on mount
    useEffect(() => {
        if (projectId && typeof window !== 'undefined') {
            const savedKey = sessionStorage.getItem(getSessionKey(projectId));
            if (savedKey) {
                lastUploadedOverlayIdRef.current = savedKey;
            }
        }
    }, [projectId]);

    const uploadThumbnail = useCallback(async () => {
        if (!projectId || isLoading || isUploadingRef.current) {
            return;
        }

        // Wait for project query to complete
        if (project === undefined) {
            return;
        }

        // If project already has a thumbnail and we haven't marked it, skip
        // This prevents re-uploading on page refresh
        if (project?.thumbnailFileId && !hasCheckedExistingThumbnailRef.current) {
            hasCheckedExistingThumbnailRef.current = true;
            console.log('[ThumbnailUpload] Project already has thumbnail, skipping upload');
            return;
        }

        // Find the first video or image overlay
        const mediaOverlay = overlays.find(
            (overlay) => overlay.type === 'video' || overlay.type === 'image'
        );

        if (!mediaOverlay) {
            return;
        }

        // Check if we already uploaded for this overlay (in this session)
        const overlayKey = `${mediaOverlay.id}-${mediaOverlay.data?.src || mediaOverlay.src}`;
        if (overlayKey === lastUploadedOverlayIdRef.current) {
            return;
        }

        try {
            isUploadingRef.current = true;
            console.log('[ThumbnailUpload] Starting thumbnail upload for project:', projectId);

            const result = await extractAndUploadThumbnail(overlays, generateUploadUrl);

            if (result?.success && result.storageId) {
                await updateProjectThumbnail({
                    projectId: projectId as Id<"projects">,
                    thumbnailFileId: result.storageId as Id<"_storage">,
                });

                lastUploadedOverlayIdRef.current = overlayKey;
                hasCheckedExistingThumbnailRef.current = true;

                // Save to sessionStorage to persist across re-renders
                if (typeof window !== 'undefined') {
                    sessionStorage.setItem(getSessionKey(projectId), overlayKey);
                }

                console.log('[ThumbnailUpload] Thumbnail uploaded successfully');
            }
        } catch (error) {
            console.error('[ThumbnailUpload] Failed to upload thumbnail:', error);
        } finally {
            isUploadingRef.current = false;
        }
    }, [projectId, overlays, isLoading, project, generateUploadUrl, updateProjectThumbnail]);

    // Debounced upload when overlays change
    useEffect(() => {
        if (!projectId || isLoading) {
            return;
        }

        // Check if there are any video/image overlays
        const hasMediaOverlay = overlays.some(
            (overlay) => overlay.type === 'video' || overlay.type === 'image'
        );

        if (!hasMediaOverlay) {
            return;
        }

        // Clear existing timeout
        if (uploadTimeoutRef.current) {
            clearTimeout(uploadTimeoutRef.current);
        }

        // Set new debounced upload
        uploadTimeoutRef.current = setTimeout(() => {
            uploadThumbnail();
        }, debounceMs);

        return () => {
            if (uploadTimeoutRef.current) {
                clearTimeout(uploadTimeoutRef.current);
            }
        };
    }, [projectId, overlays, isLoading, debounceMs, uploadThumbnail]);

    return {
        uploadThumbnail,
        isUploading: isUploadingRef.current,
    };
}
